import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn.js';

const button = cva(
  // `relative` anchors the coarse-pointer touch target in tokens.css — see `data-touch-target`
  // below. As a base class rather than a CSS rule, tailwind-merge still lets a caller reposition.
  'relative inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-pill font-semibold whitespace-nowrap transition-colors duration-[var(--duration-fast)] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-ink hover:bg-accent-hover',
        secondary: 'bg-surface text-ink ring-1 ring-rule hover:bg-surface-sunk',
        ghost: 'text-ink-muted hover:bg-surface-sunk hover:text-ink',
        // Destructive actions are visually distinct AND should be confirmed by the caller.
        danger: 'bg-danger text-white hover:opacity-90',
      },
      size: {
        sm: 'px-3 py-1.5 text-[0.8125rem]',
        md: 'px-4 py-2 text-[0.875rem]',
        lg: 'px-5 py-2.5 text-[0.9375rem]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, className, type = 'button', ...rest },
  ref,
) {
  // Default to type="button". An unspecified button inside a form submits it, which is a
  // recurring source of accidental submissions.
  return (
    <button
      ref={ref}
      type={type}
      // `sm` and `md` sit under 44px; see the touch-target rule in tokens.css.
      data-touch-target=""
      className={cn(button({ variant, size }), className)}
      {...rest}
    />
  );
});
